import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';
function AccountGroupList({ onAdd, onEdit }) {
    const { hasPermission } = usePermissionContext();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (user) setUserRole(user.role);
    }, []);

    const fetchAccountGroups = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search) setLoading(true);
            const token = sessionStorage.getItem('token');
            // Assuming the API follows the pattern we just created
            const result = await fetchApi(`/account-group?search=${search}&page=${page}&limit=${limit}`);

            if (result && Array.isArray(result)) {
                setRows(result);
                setTotalEntries(result.length);
                setTotalPages(Math.ceil(result.length / limit));
            } else if (result.accountGroups) {
                setRows(result.accountGroups);
                setTotalEntries(result.total || 0);
                setTotalPages(result.pages || 1);
            }
        } catch (error) {
            console.error('Error fetching account groups:', error);
            // toast.error('Server error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchAccountGroups(searchQuery, currentPage, itemsPerPage);
        }, 200);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage]);

    const handleEdit = (group) => {
        if (onEdit) onEdit(group);
    };

    const handleAdd = () => {
        if (onAdd) onAdd();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this account group?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = sessionStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/account-group/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (response.ok) {
                        setRows(prevData => prevData.filter(item => item._id !== id));
                        Swal.fire('Deleted!', 'Account group has been deleted.', 'success');
                    } else {
                        const errData = await response.json();
                        toast.error(errData.message || 'Failed to delete account group');
                    }
                } catch (error) {
                    console.error("Delete Error:", error);
                    toast.error("Server error");
                }
            }
        });
    };

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        ...(userRole === 'SUPER_ADMIN' ? [{ key: 'company', label: 'COMPANY', render: (item) => item.companyId?.companyName || '' }] : []),
        { key: 'accountGroupName', label: 'NAME' },
        {
            key: 'update',
            label: 'UPDATE',
            headerStyle: { textAlign: 'center' },
            style: { width: '100px', textAlign: 'center' },
            render: (item) => (
                <div className="list-page-action-icons" style={{ justifyContent: 'center' }}>
                    {hasPermission('AccountGroup', 'update') && (
                        <button type="button" className="list-page-icon-btn" title="Update" onClick={() => handleEdit(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        },
        {
            key: 'delete',
            label: 'DELETE',
            headerStyle: { textAlign: 'center' },
            style: { width: '100px', textAlign: 'center' },
            render: (item) => (
                <div className="list-page-action-icons" style={{ justifyContent: 'center' }}>
                    {hasPermission('AccountGroup', 'delete') && (
                        <button type="button" className="list-page-icon-btn" title="Delete" onClick={() => handleDelete(item._id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">Account Group List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('AccountGroup', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={handleAdd}>
                            ADD ACCOUNT GROUP
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                searchBar={
                    <SearchBar
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="Search account groups..."
                    />
                }
            />
        </div>
    );
}

export default AccountGroupList;
